import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Home, ArrowLeft, User } from "lucide-react";

interface AreaScore {
  area: string;
  score: number;
  count: number;
  globalAvg?: number;
  collegeAvg?: number;
}

interface UserProfile {
  first_name: string;
  last_name: string;
}

const AdminUserAnalytics = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [areaScores, setAreaScores] = useState<AreaScore[]>([]);
  const [stats, setStats] = useState({
    totalAttempts: 0,
    completedAttempts: 0,
    avgScore: 0,
    bestArea: "",
    weakestArea: "",
  });

  useEffect(() => {
    if (!isLoading && (!user || !isAdmin)) {
      navigate("/");
    }
  }, [user, isAdmin, isLoading, navigate]);

  useEffect(() => {
    if (user && isAdmin && userId) {
      fetchUserAnalytics();
    }
  }, [user, isAdmin, userId]);

  const fetchUserAnalytics = async () => {
    setLoading(true);

    // Fetch user profile and college
    const { data: profile } = await supabase
      .from("profiles")
      .select("first_name, last_name, college_id")
      .eq("id", userId)
      .single();

    if (profile) {
      setUserProfile(profile);
    }

    // Fetch user attempts
    const { data: attempts } = await supabase
      .from("attempts")
      .select("*")
      .eq("user_id", userId);

    if (attempts) {
      const completed = attempts.filter(a => a.completed);
      const avgScore = completed.length > 0
        ? completed.reduce((sum, a) => sum + (a.overall_score || 0), 0) / completed.length
        : 0;

      // Fetch all attempts in the system for global and college averages
      const { data: allAttempts } = await supabase
        .from("attempts")
        .select("id, user_id");

      if (allAttempts) {
        // Get attempts from user's college
        let collegeAttempts = allAttempts;
        if (profile?.college_id) {
          const { data: collegeProfiles } = await supabase
            .from("profiles")
            .select("id")
            .eq("college_id", profile.college_id);

          const collegeUserIds = new Set(collegeProfiles?.map(p => p.id));
          collegeAttempts = allAttempts.filter(a => collegeUserIds.has(a.user_id));
        }

        // Fetch responses for user, global, and college
        const userAttemptIds = attempts.map(a => a.id);
        const globalAttemptIds = allAttempts.map(a => a.id);
        const collegeAttemptIds = collegeAttempts.map(a => a.id);

        const [{ data: userResponses }, { data: globalResponses }, { data: collegeResponses }] = await Promise.all([
          supabase.from("responses").select("competence_area, rating").in("attempt_id", userAttemptIds),
          supabase.from("responses").select("competence_area, rating").in("attempt_id", globalAttemptIds),
          supabase.from("responses").select("competence_area, rating").in("attempt_id", collegeAttemptIds),
        ]);

        if (userResponses && globalResponses) {
          // Use fixed area list for grouping
          const AREAS = [
            "Information & Data Literacy",
            "Communication & Collaboration",
            "Digital Content Creation",
            "Safety",
            "Problem Solving"
          ];

          // Build area maps for user, global, and college
          const userAreaMap = new Map<string, { total: number; count: number; min: number; max: number }>();
          const globalAreaMap = new Map<string, { total: number; count: number; min: number; max: number }>();
          const collegeAreaMap = new Map<string, { total: number; count: number; min: number; max: number }>();

          AREAS.forEach(area => {
            userAreaMap.set(area, { total: 0, count: 0, min: 999, max: 0 });
            globalAreaMap.set(area, { total: 0, count: 0, min: 999, max: 0 });
            collegeAreaMap.set(area, { total: 0, count: 0, min: 999, max: 0 });
          });

          // Process user responses
          userResponses.forEach((response) => {
            if (userAreaMap.has(response.competence_area)) {
              const current = userAreaMap.get(response.competence_area)!;
              userAreaMap.set(response.competence_area, {
                total: current.total + response.rating,
                count: current.count + 1,
                min: Math.min(current.min, response.rating),
                max: Math.max(current.max, response.rating),
              });
            }
          });

          // Process global responses
          globalResponses.forEach((response) => {
            if (globalAreaMap.has(response.competence_area)) {
              const current = globalAreaMap.get(response.competence_area)!;
              globalAreaMap.set(response.competence_area, {
                total: current.total + response.rating,
                count: current.count + 1,
                min: Math.min(current.min, response.rating),
                max: Math.max(current.max, response.rating),
              });
            }
          });

          // Process college responses
          collegeResponses?.forEach((response) => {
            if (collegeAreaMap.has(response.competence_area)) {
              const current = collegeAreaMap.get(response.competence_area)!;
              collegeAreaMap.set(response.competence_area, {
                total: current.total + response.rating,
                count: current.count + 1,
                min: Math.min(current.min, response.rating),
                max: Math.max(current.max, response.rating),
              });
            }
          });

          // Calculate scores with global and college averages
          const scores: AreaScore[] = AREAS.map(area => {
            const userData = userAreaMap.get(area)!;
            const globalData = globalAreaMap.get(area)!;
            const collegeData = collegeAreaMap.get(area)!;

            let score = 0;
            let globalAvg = 0;
            let collegeAvg = 0;

            // Use global min/max as the standard scale for all normalizations
            if (globalData.count > 0) {
              const globalRatingRange = globalData.max - globalData.min;
              
              if (globalRatingRange > 0) {
                if (userData.count > 0) {
                  const userAvg = userData.total / userData.count;
                  score = ((userAvg - globalData.min) / globalRatingRange) * 100;
                  score = Math.max(0, Math.min(100, Math.round(score)));
                }

                const gAvg = globalData.total / globalData.count;
                globalAvg = ((gAvg - globalData.min) / globalRatingRange) * 100;
                globalAvg = Math.max(0, Math.min(100, Math.round(globalAvg)));

                if (collegeData.count > 0) {
                  const cAvg = collegeData.total / collegeData.count;
                  collegeAvg = ((cAvg - globalData.min) / globalRatingRange) * 100;
                  collegeAvg = Math.max(0, Math.min(100, Math.round(collegeAvg)));
                }
              }
            }

            return {
              area,
              score,
              count: userData.count,
              globalAvg,
              collegeAvg,
            };
          });

          setAreaScores(scores);

          const sortedScores = [...scores].filter(s => s.count > 0).sort((a, b) => b.score - a.score);

          setStats({
            totalAttempts: attempts.length,
            completedAttempts: completed.length,
            avgScore,
            bestArea: sortedScores[0]?.area || "N/A",
            weakestArea: sortedScores[sortedScores.length - 1]?.area || "N/A",
          });
        }
      }
    }

    setLoading(false);
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const radarData = areaScores.map(area => ({
    area: area.area,
    score: area.score,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Bilingual Header */}
        <Card className="p-6 mb-6 bg-primary/5 border-primary/20">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                User Analytics - تحليلات المستخدم
              </h1>
              <p className="text-lg text-foreground">
                {userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : "Loading..."}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate(`/admin/user-dashboard/${userId}`)}
                variant="outline"
                className="gap-2"
              >
                <User className="h-4 w-4" />
                Dashboard - لوحة التحكم
              </Button>
              <Button
                onClick={() => navigate("/admin/participants")}
                variant="outline"
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back - رجوع
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="default"
                className="gap-2"
              >
                <Home className="h-4 w-4" />
                Home - الرئيسية
              </Button>
            </div>
          </div>
        </Card>

        {/* Statistics Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              Total Attempts - المحاولات
            </h3>
            <p className="text-2xl font-bold text-primary">{stats.totalAttempts}</p>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              Completed - المكتمل
            </h3>
            <p className="text-2xl font-bold text-success">{stats.completedAttempts}</p>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              Average Score - المعدل
            </h3>
            <p className="text-2xl font-bold text-accent">{stats.avgScore.toFixed(1)}%</p>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              Best Area - الأفضل
            </h3>
            <p className="text-sm font-bold text-success">{stats.bestArea}</p>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              Weakest Area - الأضعف
            </h3>
            <p className="text-sm font-bold text-destructive">{stats.weakestArea}</p>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4 text-primary" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              Competence Radar - رادار الكفاءة
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="area" tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Radar
                  name="Score"
                  dataKey="score"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary))"
                  fillOpacity={0.6}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "var(--radius)",
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6">
            <h3 className="text-xl font-bold mb-4 text-primary" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              Scores by Area - النتائج حسب المجال
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={areaScores} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="area" 
                  tick={{ fill: "hsl(var(--foreground))", fontSize: 10 }} 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "var(--radius)",
                  }}
                />
                <Legend />
                <Bar dataKey="score" fill="#3b82f6" name="My Score %" barSize={50} />
                <Bar dataKey="globalAvg" fill="#10b981" name="Global Avg %" barSize={50} />
                <Bar dataKey="collegeAvg" fill="#f59e0b" name="College Avg %" barSize={50} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Detailed Scores Table */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-4 text-primary" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            Detailed Scores - النتائج التفصيلية
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                    Area - المجال
                  </th>
                  <th className="text-right py-3 px-4 font-semibold" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                    Score - النتيجة
                  </th>
                  <th className="text-right py-3 px-4 font-semibold" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                    Questions - الأسئلة
                  </th>
                </tr>
              </thead>
              <tbody>
                {areaScores.map((area, index) => (
                  <tr key={index} className="border-b border-border/50">
                    <td className="py-3 px-4">{area.area}</td>
                    <td className="py-3 px-4 text-right font-bold">
                      {area.score.toFixed(1)}%
                    </td>
                    <td className="py-3 px-4 text-right text-muted-foreground">
                      {area.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminUserAnalytics;
